import { mapToElementState } from '@src/renderer/ElementState';
import { useVideoCreatorStore } from '@src/stores-v2';
import parsePercent from '@src/utility/parsePercent';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LogManager } from '../../utility/Logger';

import type { LayoutRepresentationBinding } from '@src/editor/models/layout/types';
import type { ElementState } from '@src/renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

const logger = LogManager.getLogger('useLayoutAwareVideoSettingsState');

type ReturnType = {
  activeTimelineVideoElement: ElementState;
  hiddenParentElement: ElementState | null;
  isLayoutComposition: boolean;
  childVideoElement: ElementState | null;
  displayElement: ElementState;
  sourceUrl: string;
  xValue: number;
  yValue: number;
  updateTimelineVideoProperty: (
    propertyUpdates: Record<string, any>,
  ) => Promise<void>;
  modifyPropertyWithSlider: (propertyName: string, newValue: number) => void;
  handleChangeScale: (value: number) => void;
};

export default function useLayoutAwareVideoSettingsState({
  activeElement,
  editorStore,
}: {
  editorStore: EditorStoreLike;
  activeElement: ElementState;
}): ReturnType {
  const videoCreator = useVideoCreatorStore();
  const videoSourceStore = videoCreator.rootStore.videoSourceStore;

  const [hiddenParentElement, setHiddenParentElement] =
    useState<ElementState | null>(null);
  const [isLayoutComposition, setIsLayoutComposition] =
    useState<boolean>(false);
  const [childVideoElement, setChildVideoElement] =
    useState<ElementState | null>(null);
  const [activeLayoutBinding, setActiveLayoutBinding] =
    useState<LayoutRepresentationBinding | null>(null);
  // Keep track of slider values to prevent UI from snapping
  const [sliderValues, setSliderValues] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });

  const layoutIndexStore = videoCreator.rootStore.sceneLayoutIndexStore;
  const layoutRecordsRef = layoutIndexStore.recordsByElementId;
  const sourceRef = videoSourceStore.source;
  const extraElementDataRef = videoCreator.currentVideo?.extraElementData;

  const resolvedDisplayElement = useMemo(() => {
    try {
      return videoCreator.sceneLayoutSelectionManager.resolveElementForActivation(
        activeElement,
      );
    } catch (_error) {
      return activeElement;
    }
  }, [
    videoCreator.sceneLayoutSelectionManager,
    activeElement,
    layoutRecordsRef,
  ]);

  const getElementStateById = useCallback(
    (elementId?: string | null): ElementState | null => {
      if (!elementId) {
        return null;
      }

      const elementSource = editorStore.editor
        .getElementById(elementId)
        ?.toJSON();
      if (!elementSource) {
        return null;
      }

      return mapToElementState(elementSource);
    },
    [editorStore.editor],
  );

  const resolveBindingForId = useCallback(
    (elementId?: string | null) => {
      if (!elementId) {
        return undefined;
      }

      const records = layoutIndexStore.getRecords(elementId);
      if (!records || records.length === 0) {
        return undefined;
      }

      const representationRecord = records.find(
        record => record.role === 'representation',
      );
      if (representationRecord) {
        return representationRecord.link;
      }

      const parentRecord = records.find(record => record.role === 'parent');
      if (parentRecord) {
        return parentRecord.link;
      }

      const compositionRecord = records.find(
        record => record.role === 'composition',
      );
      return compositionRecord?.link;
    },
    [layoutIndexStore],
  );

  const applySceneLayoutIndexContext = useCallback((): boolean => {
    const candidateIds = [
      resolvedDisplayElement?.source?.id,
      activeElement?.source?.id,
    ].filter((id): id is string => Boolean(id));

    for (const candidateId of candidateIds) {
      const binding = resolveBindingForId(candidateId);
      if (!binding) {
        continue;
      }

      const parentElement =
        getElementStateById(binding.parentId) ?? activeElement;
      const representationElement =
        getElementStateById(binding.representationId) ??
        (binding.representationId === resolvedDisplayElement.source.id
          ? resolvedDisplayElement
          : null);

      if (!representationElement) {
        continue;
      }

      setActiveLayoutBinding(binding);
      setIsLayoutComposition(true);
      setHiddenParentElement(parentElement);
      setChildVideoElement(representationElement);
      return true;
    }

    setActiveLayoutBinding(null);
    return false;
  }, [
    activeElement,
    resolvedDisplayElement,
    resolveBindingForId,
    getElementStateById,
  ]);

  const applyLegacyLayoutDetection = useCallback(() => {
    const elementId = activeElement.source?.id;
    if (!elementId) {
      setIsLayoutComposition(false);
      setHiddenParentElement(null);
      setChildVideoElement(null);
      setActiveLayoutBinding(null);
      return;
    }

    const videoChild =
      activeElement.source.type === 'composition'
        ? (activeElement.elements?.find(el => el.source.type === 'video') ??
          null)
        : null;

    setIsLayoutComposition(false);
    setHiddenParentElement(null);
    setChildVideoElement(videoChild);
    setActiveLayoutBinding(null);
  }, [activeElement]);

  useEffect(() => {
    if (applySceneLayoutIndexContext()) {
      return;
    }
    applyLegacyLayoutDetection();
  }, [
    applySceneLayoutIndexContext,
    applyLegacyLayoutDetection,
    layoutRecordsRef,
    sourceRef,
    extraElementDataRef,
  ]);

  const expandAlignmentProperties = (
    propertyUpdates: Record<string, any>,
  ): Record<string, any> => {
    const expanded = { ...propertyUpdates };
    // for (const key in propertyUpdates) {
    //   if (key === 'x') {
    //     expanded.x_anchor = propertyUpdates[key];
    //   }
    //   if (key === 'y') {
    //     expanded.y_anchor = propertyUpdates[key];
    //   }
    // }
    return expanded;
  };

  const layoutTransformKeys = new Set([
    'x',
    'y',
    'width',
    'height',
    'x_anchor',
    'y_anchor',
    'x_alignment',
    'y_alignment',
    'x_scale',
    'y_scale',
  ]);

  const affectsLayoutBinding = (updates: Record<string, any>): boolean =>
    Object.keys(updates).some(key => layoutTransformKeys.has(key));

  const commandUpdateElementDebounced = useCallback(
    debounce((elementId: string, propertyUpdates: Record<string, any>) => {
      editorStore.editor.commandUpdateElement(elementId, propertyUpdates);
    }, 250),
    [editorStore.editor],
  );

  const applyExpandedUpdatesToDisplayElement = useCallback(
    (expandedUpdates: Record<string, any>) => {
      const elementToModify =
        childVideoElement || resolvedDisplayElement || activeElement;
      if (!elementToModify) {
        return;
      }

      if (
        isLayoutComposition &&
        activeLayoutBinding &&
        affectsLayoutBinding(expandedUpdates)
      ) {
        const nextElement: ElementState = {
          ...elementToModify,
          source: {
            ...elementToModify.source,
            ...expandedUpdates,
          },
        };

        if (
          !editorStore.rendererSyncService?.applyManualRepresentationUpdate(
            nextElement,
          )
        ) {
          logger.warn(
            'applyManualRepresentationUpdateDebounced failed for element',
            nextElement,
          );
        }

        return;
      }

      commandUpdateElementDebounced(elementToModify.source.id, expandedUpdates);
    },
    [
      childVideoElement,
      resolvedDisplayElement,
      activeElement,
      isLayoutComposition,
      activeLayoutBinding,
      editorStore.rendererSyncService,
    ],
  );

  const applyExpandedUpdatesToDisplayElementDebounced = useCallback(
    debounce((expandedUpdates: Record<string, any>) => {
      applyExpandedUpdatesToDisplayElement(expandedUpdates);
    }, 250),
    [applyExpandedUpdatesToDisplayElement],
  );

  // Helper function to update video element properties using headless editor
  const updateVideoProperty = async (propertyUpdates: Record<string, any>) => {
    const expandedUpdates = expandAlignmentProperties(propertyUpdates);
    applyExpandedUpdatesToDisplayElement(expandedUpdates);
  };

  const updateTimelineVideoProperty = async (
    propertyUpdates: Record<string, any>,
  ) => {
    const expandedUpdates = expandAlignmentProperties(propertyUpdates);

    if (
      isLayoutComposition &&
      activeLayoutBinding &&
      affectsLayoutBinding(expandedUpdates)
    ) {
      applyExpandedUpdatesToDisplayElementDebounced(expandedUpdates);
      return;
    }

    const elementToModify = getActiveTimelineVideoElement();
    commandUpdateElementDebounced(elementToModify.source.id, expandedUpdates);
  };

  // Function to update slider values from the current element properties
  // Using a function in the setState to avoid dependency on sliderValues
  const updateSliderValuesFromElement = useCallback(() => {
    const element =
      childVideoElement || resolvedDisplayElement || activeElement;
    if (element && element.source) {
      setSliderValues(prevValues => {
        const newValues = { ...prevValues };

        // For X alignment, prefer x_alignment if available, then x
        if (element.source.x_alignment !== undefined) {
          const xString = element.source.x_alignment.toString();
          const xVal = parseFloat(xString);
          if (!isNaN(xVal)) newValues.x = xVal;
        } else if (element.source.x !== undefined) {
          const xString = element.source.x.toString();
          const xVal = parseFloat(xString);
          if (!isNaN(xVal)) newValues.x = xVal;
        } else {
          newValues.x = 50; // Default value
        }

        // For Y alignment, prefer y_alignment if available, then y
        if (element.source.y_alignment !== undefined) {
          const yString = element.source.y_alignment.toString();
          const yVal = parseFloat(yString);
          if (!isNaN(yVal)) newValues.y = yVal;
        } else if (element.source.y !== undefined) {
          const yString = element.source.y.toString();
          const yVal = parseFloat(yString);
          if (!isNaN(yVal)) newValues.y = yVal;
        } else {
          newValues.y = 50; // Default value
        }

        return newValues;
      });
    }
  }, [(childVideoElement || resolvedDisplayElement || activeElement)?.source]);

  // Update slider values when the child/active video element changes
  useEffect(() => {
    updateSliderValuesFromElement();
  }, [updateSliderValuesFromElement]);

  // Modified function to immediately update the UI and then debounce the API call
  const modifyPropertyWithSlider = (propertyName: string, newValue: number) => {
    // Update local state immediately for smooth UI
    const updates: Record<string, string> = {};
    if (propertyName === 'x') {
      setSliderValues(prev => ({ ...prev, x: newValue }));
      updates.x = `${newValue}%`;
    } else if (propertyName === 'y') {
      setSliderValues(prev => ({ ...prev, y: newValue }));
      updates.y = `${newValue}%`;
      updates.y_anchor = `${100 - newValue}%`;
    }

    // // Convert to percentage string (without spaces)
    // const propertyValue = `${newValue}%`;
    // updates[propertyName] = propertyValue;

    updateVideoProperty(updates);
  };

  const handleChangeScale = debounce(async (value: number) => {
    const elementToModify =
      childVideoElement || resolvedDisplayElement || activeElement;
    const source = elementToModify?.source;
    if (!source) return;

    // Get current width/height, x/y, x_anchor/y_anchor
    const width = parsePercent(source.width) ?? 100;
    const height = parsePercent(source.height) ?? 100;
    const x = parsePercent(source.x) ?? 50;
    const y = parsePercent(source.y) ?? 50;
    const x_anchor = parsePercent(source.x_anchor ?? source.x_alignment) ?? 50;
    const y_anchor = parsePercent(source.y_anchor ?? source.y_alignment) ?? 50;

    // Calculate the visual center in composition coordinates
    const centerX = x + width * (0.5 - x_anchor / 100);
    const centerY = y + height * (0.5 - y_anchor / 100);

    // New width/height
    const newWidth = value;
    const newHeight = value;

    // To keep the center fixed, solve for new x/y:
    // centerX = newX + (newWidth * (0.5 - x_anchor/100))
    // => newX = centerX - newWidth * (0.5 - x_anchor/100)
    // same for y
    const newX = centerX - newWidth * (0.5 - x_anchor / 100);
    const newY = centerY - newHeight * (0.5 - y_anchor / 100);

    // Only update width, height, x, y. Do not touch alignment/anchor properties.
    const updates: Record<string, string> = {
      width: `${newWidth}%`,
      height: `${newHeight}%`,
      x: `${newX}%`,
      y: `${newY}%`,
    };

    const expandedUpdates = expandAlignmentProperties(updates);
    applyExpandedUpdatesToDisplayElement(expandedUpdates);
  }, 250);

  // Determine which element to use for different properties
  const displayElement =
    childVideoElement || resolvedDisplayElement || activeElement;

  // Update slider values directly based on current state
  // Do this in render to ensure it's always up-to-date
  const activeElementForSliders =
    childVideoElement || resolvedDisplayElement || activeElement;

  // Set x value directly from the source
  const xValue = (() => {
    if (!activeElementForSliders?.source) return 50;

    if (sliderValues.x != null) {
      return sliderValues.x;
    }

    // Try x_alignment first
    if (activeElementForSliders.source.x_alignment !== undefined) {
      const val = parseFloat(
        activeElementForSliders.source.x_alignment.toString(),
      );
      if (!isNaN(val)) return val;
    }

    // Then try x
    if (activeElementForSliders.source.x !== undefined) {
      const val = parseFloat(activeElementForSliders.source.x.toString());
      if (!isNaN(val)) return val;
    }

    return 50; // Default
  })();

  // Set y value directly from the source
  const yValue = (() => {
    if (!activeElementForSliders?.source) return 50;

    if (sliderValues.y != null) {
      return sliderValues.y;
    }

    // Try y_alignment first
    if (activeElementForSliders.source.y_alignment !== undefined) {
      const val = parseFloat(
        activeElementForSliders.source.y_alignment.toString(),
      );
      if (!isNaN(val)) return val;
    }

    // Then try y
    if (activeElementForSliders.source.y !== undefined) {
      const val = parseFloat(activeElementForSliders.source.y.toString());
      if (!isNaN(val)) return val;
    }

    return 50; // Default
  })();

  const sourceUrl = displayElement.source.source;

  return {
    activeTimelineVideoElement: getActiveTimelineVideoElement(),
    hiddenParentElement,
    isLayoutComposition,
    childVideoElement,
    displayElement,
    sourceUrl,
    xValue,
    yValue,
    updateTimelineVideoProperty,
    modifyPropertyWithSlider,
    handleChangeScale,
  };

  function getActiveTimelineVideoElement() {
    return isLayoutComposition && hiddenParentElement
      ? hiddenParentElement
      : activeElement;
  }
}
